/* ============================================================
   Trasse Families — public signup at /families/start.

   One question per screen, four screens. A parent signing up alone has
   never heard of a "driver account", a "geofence" or an "org code", so
   every screen asks one thing and shows what it means; the right-hand
   panel shows what the DRIVER will see, because the thing most parents
   are quietly worried about is what they are asking their driver to
   accept.

   The pilot takes NO card. Nothing here touches Paystack — it posts once
   to /api/families/pilot/start and the household does not exist until the
   parent clicks the link in their inbox.

   Copy rule: the child is the reason, the VEHICLE is what is tracked.
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import LocationPicker, { LatLng } from './LocationPicker';
import { buildFamilyOrgCode } from '../fleet/fleetConstants';
import {
  startFamilyPilot,
  checkFamilySignup,
  resendFamilyActivation,
  FamilyDriverMode,
  FamilyPilotStartBody,
  lookupFamilyReferrer,
} from '../services/paymentsApi';
import {
  FAM, FAM_GRADIENT, FAMILY_FONT_CSS, UI_FONT, DISPLAY_FONT,
  FAMILY_PILOT_DAYS, FAMILY_PRICE_LABEL, FAMILY_ADDON_KES, FAMILY_CHILD_CAP_HINT,
  TRACKING_ON, TRACKING_OFF,
} from './family/familyTheme';
import { SUPPORT_EMAIL } from '../utils/companyContact';

/* ---------------------------------------------------------------- rules --
   Client-side mirrors of pear-payments-backend/src/families/signup.js. The
   server is authoritative; these exist so a parent is told about a typo
   before they wait on a round trip, not instead of the server checking. */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PLATE_RE = /^[A-Z]{3}[0-9]{3}[A-Z]$/;
const HHMM_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
const MAX_CHILDREN = 2;

const WEEKDAYS: { key: string; label: string }[] = [
  { key: 'mon', label: 'Mon' }, { key: 'tue', label: 'Tue' }, { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' }, { key: 'fri', label: 'Fri' }, { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const FUEL_TYPES = [
  { id: 'petrol', label: 'Petrol' },
  { id: 'diesel', label: 'Diesel' },
  { id: 'hybrid', label: 'Hybrid' },
  { id: 'electric', label: 'Electric' },
];

function normalizePlate(v: string): string {
  return (v || '').toUpperCase().replace(/[\s-]/g, '');
}

/** The 9-digit Kenyan national tail, so 0722…, +254722… and 722… are one number. */
function phoneTail(v: string): string {
  const digits = (v || '').replace(/[^0-9]/g, '');
  const tail = digits.replace(/^0+/, '').replace(/^254/, '');
  return tail.length >= 9 ? tail.slice(-9) : tail;
}

function nationalDigits(v: string): string {
  return (v || '').replace(/[^0-9]/g, '').replace(/^0+/, '');
}

const FAMILY_DIALS: { dial: string; iso: string; name: string }[] = [
  { dial: '+254', iso: 'KE', name: 'Kenya' },
  { dial: '+256', iso: 'UG', name: 'Uganda' },
  { dial: '+255', iso: 'TZ', name: 'Tanzania' },
  { dial: '+250', iso: 'RW', name: 'Rwanda' },
  { dial: '+1', iso: 'US', name: 'United States' },
  { dial: '+44', iso: 'GB', name: 'United Kingdom' },
];

function e164(dial: string, v: string): string {
  if (dial === '+254') return `+254${phoneTail(v)}`;
  return `${dial}${nationalDigits(v)}`;
}

function phoneLooksReachable(dial: string, v: string): boolean {
  if (dial === '+254') return phoneTail(v).length >= 9;
  return nationalDigits(v).length >= 7;
}

const dialSelectStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', padding: '0 10px', borderRadius: 12,
  background: FAM.tint, color: FAM.accent, fontSize: 14.5, fontWeight: 700,
  border: 'none', fontFamily: 'inherit', cursor: 'pointer',
};

/** OKELLO → OKELLO.family. Sanitised exactly as familyOrgCodeFrom() does on the
 *  server, so what we show live under the field is what the household is called. */
function orgCodeFor(householdName: string): string {
  const base = (householdName || '').toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 32);
  if (!base) return '';
  const { code, error } = buildFamilyOrgCode(base);
  return error ? '' : code;
}

/** Which screen owns a server-rejected field, so we can land the parent on it. */
const FIELD_STEP: Record<string, number> = {
  parentName: 1, email: 1, phone: 1, householdName: 1, children: 1, home: 1,
  driverMode: 2, driverName: 2, driverEmail: 2, driverPhone: 2, vehiclePlate: 2,
  vanCompany: 2, vanSeats: 2, vanPhone: 2,
  schedule: 3,
};

interface ChildRow { name: string; classLabel: string }
type Phase = 'form' | 'sending' | 'done';

/* ------------------------------------------------------------- fragments --
   Every helper lives at MODULE scope. Declared inside the component they get a
   fresh identity on each keystroke, React remounts the field, and focus jumps
   away mid-word (the bug PublicActivate documents at the top of its file). */

const Field: React.FC<{
  label: string; hint?: React.ReactNode; error?: string | null; children: React.ReactNode; style?: React.CSSProperties;
}> = ({ label, hint, error, children, style }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
    <label style={{ fontSize: 12.5, fontWeight: 700, color: FAM.ink3, letterSpacing: '.01em' }}>{label}</label>
    {children}
    {error
      ? <span style={{ fontSize: 12.5, fontWeight: 600, color: FAM.danger }}>{error}</span>
      : hint ? <span style={{ fontSize: 12.5, color: FAM.muted }}>{hint}</span> : null}
  </div>
);

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.12em', color: FAM.accent }}>{children}</span>
);

const Lede: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ margin: '12px 0 0', fontSize: 15.5, lineHeight: 1.55, color: FAM.ink2, maxWidth: 430 }}>{children}</p>
);

const CheckIcon: React.FC<{ on: boolean }> = ({ on }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={on ? FAM.pos : FAM.ink3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 18px' }}>
    <circle cx="12" cy="12" r="9.5" />
    {on && <path d="M8 12.4l2.6 2.6L16.2 9.4" />}
  </svg>
);

const Arrow: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h13M13 6l6 6-6 6" /></svg>
);

/** The two ways a household actually gets to school. */
const DRIVER_CARDS: { id: FamilyDriverMode; title: string; sub: string; soon?: boolean; icon: React.ReactNode }[] = [
  {
    id: 'private_driver', title: 'A driver I employ', sub: "I'll invite them by phone number",
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l7.5 3.2v5c0 4.4-3 8.2-7.5 9.6-4.5-1.4-7.5-5.2-7.5-9.6v-5L12 3Z" /><path d="M9.3 12.2l1.9 1.9 3.6-3.8" /></svg>,
  },
  {
    id: 'private_van', title: 'A private van service', sub: 'Shared with other families',
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 16V9.5a2 2 0 0 1 2-2h8l4 4h2a2 2 0 0 1 2 2V16" /><path d="M3 16h2.2M18.8 16H21M13 7.5V16H8.2" /><circle cx="7" cy="17.2" r="1.8" /><circle cx="17" cy="17.2" r="1.8" /></svg>,
  },
];

/* ============================================================ component == */
export default function FamilyOnboarding() {
  /* Read once — a parent who navigates within the flow must not lose the code
     they arrived on. The backend now RESOLVES it: the introduction is recorded
     at activation, permanently, and cannot be reconstructed afterwards. */
  const referralFromLink = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return (new URLSearchParams(window.location.search).get('ref') || '').trim().toUpperCase();
  }, []);
  /* TYPED, not just read off the link.
     The code used to be link-only: whatever was in ?ref= and nothing else.
     But the inviting parent is shown a CODE to copy — the app's own dialog
     says "share this code with the other family" — and read out over the
     phone or pasted into WhatsApp as bare letters it had nowhere to go. The
     new household simply arrived at the site with no ?ref=, the introduction
     was never recorded, and the backend cannot reconstruct one afterwards:
     the referrer silently lost the reward they were promised (Gabriel,
     09-06). Seeded from the link when there is one, typeable when there
     isn't. */
  const [referralCode, setReferralCode] = useState(referralFromLink);

  /* Who invited them, resolved server-side — firestore.rules deliberately
     refuses to let a signed-out page read referralCodes, and the label is the
     only field that ever leaves the server (never an email or a phone). */
  const [referrer, setReferrer] = useState<{ label: string; kind: string } | null>(null);
  /** null = not looked up yet; false = looked up and no such code. Only a
   *  definite `false` is worth telling the parent about — a half-typed code
   *  is not a wrong one. */
  const [referralKnown, setReferralKnown] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    const code = referralCode.trim();
    setReferrer(null);
    setReferralKnown(null);
    // A code is at least this long; below it they are still typing and every
    // keystroke would be a lookup.
    if (code.length < 4) return;
    // Debounced: one call per settled code, not one per keystroke.
    const t = window.setTimeout(async () => {
      const res = await lookupFamilyReferrer(code);
      if (!alive) return;
      if (res.ok && res.label) {
        setReferrer({ label: res.label, kind: res.kind || 'parent' });
        setReferralKnown(true);
      } else {
        setReferralKnown(false);
      }
    }, 400);
    return () => { alive = false; window.clearTimeout(t); };
  }, [referralCode]);

  const [step, setStep] = useState(1);
  const [phase, setPhase] = useState<Phase>('form');
  /** Server rejection: `field` decides where it renders, `message` is verbatim. */
  const [serverErr, setServerErr] = useState<{ field?: string; message: string } | null>(null);
  /** Only show "you left this blank" once they have tried to move on. */
  const [touched, setTouched] = useState<Record<number, boolean>>({});
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{ orgCode: string; email: string; emailed: boolean } | null>(null);

  /* The "check your email" screen's resend. A parent whose link landed in spam
     cannot simply sign up again — their address is claimed for life by the
     one-trial guard — so this is their way back in. A minute between presses,
     because the server only allows three in an hour. */
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendNote, setResendNote] =
    useState<{ tone: 'sent' | 'settled' | 'bad'; text: string } | null>(null);
  const resendCooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    if (resendCooldownRef.current) clearInterval(resendCooldownRef.current);
  }, []);

  /* step 1 — you & your child */
  const [parentName, setParentName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneDial, setPhoneDial] = useState('+254');
  const [householdName, setHouseholdName] = useState('');
  const [children, setChildren] = useState<ChildRow[]>([{ name: '', classLabel: '' }]);
  const [home, setHome] = useState<LatLng | null>(null);
  const [schoolName, setSchoolName] = useState('');
  const [school, setSchool] = useState<LatLng | null>(null);
  const [showSchoolPin, setShowSchoolPin] = useState(false);

  /* step 2 — who drives */
  const [driverMode, setDriverMode] = useState<FamilyDriverMode | ''>('');
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [driverDial, setDriverDial] = useState('+254');
  const [driverEmail, setDriverEmail] = useState('');
  const [plate, setPlate] = useState('');
  const [nickname, setNickname] = useState('');
  const [fuelType, setFuelType] = useState('petrol');
  const [vanCompany, setVanCompany] = useState('');
  const [vanSeats, setVanSeats] = useState('');
  const [vanPhone, setVanPhone] = useState('');
  const [vanDial, setVanDial] = useState('+254');

  /* step 3 — hours */
  const [days, setDays] = useState<string[]>(['mon', 'tue', 'wed', 'thu', 'fri']);
  const [morningAt, setMorningAt] = useState('07:10');
  const [eveningAt, setEveningAt] = useState('16:00');
  /** Which runs the car does — both, or only one (Gabriel, 2026-10-28). */
  const [runs, setRuns] = useState<'both' | 'morning' | 'evening'>('both');

  const orgCode = orgCodeFor(householdName);
  const cleanPlate = normalizePlate(plate);
  const firstChild = children[0] || { name: '', classLabel: '' };

  /* ------------------------------------------------------------ validity -- */
  const step1Errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (parentName.trim().length < 2) e.parentName = 'Please give your name.';
    if (!EMAIL_RE.test(email.trim().toLowerCase())) e.email = 'That email address does not look right.';
    if (!phoneLooksReachable(phoneDial, phone)) e.phone = 'Please give a phone number we can reach you on.';
    if (!orgCode) e.householdName = 'Please give your family a name.';
    if (children.some((c) => c.name.trim().length < 2)) e.children = 'Each child needs a name.';
    if (!home) e.home = 'Drop a pin on your home.';
    return e;
  }, [parentName, email, phone, phoneDial, orgCode, children, home]);

  const step2Errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (driverMode !== 'private_driver' && driverMode !== 'private_van') {
      e.driverMode = 'Tell us who drives.';
    }
    if (!PLATE_RE.test(cleanPlate)) e.vehiclePlate = 'That does not look like a Kenyan number plate (e.g. KDA442K).';
    if (driverMode === 'private_driver') {
      if (driverName.trim().length < 2) e.driverName = "Give your driver's name.";
      if (!EMAIL_RE.test(driverEmail.trim().toLowerCase())) {
        e.driverEmail = "We need your driver's email — that is how they sign in to the app.";
      }
    }
    if (driverMode === 'private_van' && vanCompany.trim().length < 2) {
      e.vanCompany = 'Give the van service its name.';
    }
    return e;
  }, [driverMode, cleanPlate, driverName, driverEmail, vanCompany]);

  const step3Errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!days.length) e.schedule = 'Pick at least one day.';
    if (!HHMM_RE.test(morningAt)) e.schedule = 'Morning pickup time must look like 07:10.';
    if (!HHMM_RE.test(eveningAt)) e.schedule = 'Afternoon time must look like 16:00.';
    return e;
  }, [days, morningAt, eveningAt]);

  const errorsFor = (n: number): Record<string, string> =>
    n === 1 ? step1Errors : n === 2 ? step2Errors : n === 3 ? step3Errors : {};
  const stepOk = (n: number) => Object.keys(errorsFor(n)).length === 0;

  /** Inline error for one input: the server's verdict wins over ours, and ours
   *  only appears once they have tried to leave the screen. */
  const errFor = (field: string, n: number): string | null => {
    if (serverErr?.field === field) return serverErr.message;
    if (!touched[n]) return null;
    return errorsFor(n)[field] || null;
  };

  /* ------------------------------------------------------------- actions -- */
  const clearServerErr = useCallback(() => setServerErr(null), []);

  function goTo(n: number) {
    setStep(n);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function next() {
    setTouched((t) => ({ ...t, [step]: true }));
    if (!stepOk(step)) return;
    if (step === 1 || step === 2) {
      setChecking(true);
      try {
        const res = await checkFamilySignup(step === 1
          ? {
            email: email.trim().toLowerCase(),
            phone: e164(phoneDial, phone),
            householdName: householdName.trim(),
          }
          : { vehiclePlate: cleanPlate });
        // Only a 400 or 409 that names a field is something the parent can fix
        // on this page. A 429, a 5xx, Render's cold-start 502, an old server's
        // 404 or no answer at all used to hold Continue shut — and showed the
        // raw 500 text — for a lookup that creates nothing: start re-checks
        // every one of these fields (review, 2026-09-14).
        if (!res.ok && (res.status === 400 || res.status === 409) && res.field) {
          setServerErr({ field: res.field, message: res.message || 'That is already in use.' });
          const target = res.field ? FIELD_STEP[res.field] : step;
          if (target) goTo(target);
          return;
        }
      } finally {
        setChecking(false);
      }
    }
    setServerErr(null);
    goTo(Math.min(4, step + 1));
  }

  function back() {
    setServerErr(null);
    goTo(Math.max(1, step - 1));
  }

  function setChild(i: number, patch: Partial<ChildRow>) {
    setChildren((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
    clearServerErr();
  }

  function buildBody(mode: FamilyDriverMode): FamilyPilotStartBody {
    return {
      parentName: parentName.trim(),
      email: email.trim().toLowerCase(),
      phone: e164(phoneDial, phone),
      householdName: householdName.trim(),
      children: children
        .filter((c) => c.name.trim())
        .map((c) => ({ name: c.name.trim(), classLabel: c.classLabel.trim() || undefined })),
      driverMode: mode,
      ...(mode === 'private_driver'
        ? {
          driverName: driverName.trim(),
          driverPhone: phoneLooksReachable(driverDial, driverPhone) ? e164(driverDial, driverPhone) : undefined,
          driverEmail: driverEmail.trim().toLowerCase() || undefined,
        }
        : {}),
      ...(mode === 'private_van'
        ? {
          vanCompany: vanCompany.trim(),
          vanSeats: Number.parseInt(vanSeats.trim(), 10) || undefined,
          vanPhone: phoneLooksReachable(vanDial, vanPhone) ? e164(vanDial, vanPhone) : undefined,
        }
        : {}),
      vehiclePlate: cleanPlate,
      vehicleNickname: nickname.trim() || undefined,
      fuelType,
      home: home as LatLng,
      school: school,
      schoolName: schoolName.trim() || undefined,
      schedule: { morningAt, eveningAt, days, runs },
      referralCode: referralCode || undefined,
    };
  }

  async function submit() {
    setTouched({ 1: true, 2: true, 3: true, 4: true });
    for (const n of [1, 2, 3]) {
      if (!stepOk(n)) { goTo(n); return; }
    }
    if (driverMode !== 'private_driver' && driverMode !== 'private_van') { goTo(2); return; }

    setPhase('sending');
    setServerErr(null);
    const res = await startFamilyPilot(buildBody(driverMode));
    if (!res.ok) {
      setPhase('form');
      setServerErr({ field: res.field, message: res.message || 'We could not start your free days.' });
      const target = res.field ? FIELD_STEP[res.field] : undefined;
      if (target) goTo(target);
      return;
    }
    setResult({
      orgCode: res.orgCode || orgCode,
      email: res.email || email.trim().toLowerCase(),
      emailed: res.emailed !== false,
    });
    setPhase('done');
    if (typeof window !== 'undefined') window.scrollTo({ top: 0 });
  }

  function startResendCooldown() {
    if (resendCooldownRef.current) clearInterval(resendCooldownRef.current);
    setResendCooldown(60);
    resendCooldownRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          if (resendCooldownRef.current) {
            clearInterval(resendCooldownRef.current);
            resendCooldownRef.current = null;
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  /** One confirmation for both answers. The server says exactly the same thing
   *  for an address it does not recognise, on purpose — so nobody can use this
   *  to find out who has signed up — and we must not give that away by wording
   *  the two differently. Nothing here is an error state: it is a parent whose
   *  email has not arrived. */
  async function handleResend() {
    if (!result || resending || resendCooldown > 0) return;
    setResending(true);
    setResendNote(null);
    const res = await resendFamilyActivation(result.email, result.orgCode);
    setResending(false);
    if (res.ok) {
      setResendNote({ tone: 'sent', text: `Sent again to ${result.email}. It can take a minute to arrive.` });
      startResendCooldown();
      return;
    }
    if (res.status === 409) {
      // Their household is already live. The server's wording, word for word.
      setResendNote({ tone: 'settled', text: res.message || 'That household is already set up — just sign in.' });
      startResendCooldown();
      return;
    }
    // A refusal we cannot fix by waiting a minute (offline, rate limit) — leave
    // the button live so they can try again once the cause has passed.
    setResendNote({
      tone: 'bad',
      text: res.message || 'We could not send that email again. Please try in a few minutes.',
    });
  }

  /* ------------------------------------------------------------ checklist -- */
  const hoursLine = `${days.length === 5 && days.every((d) => ['mon', 'tue', 'wed', 'thu', 'fri'].includes(d))
    ? 'Mon–Fri, weekends off'
    : days.length ? WEEKDAYS.filter((d) => days.includes(d.key)).map((d) => d.label).join(' ') : 'No days yet'
    } · ${runs === 'morning' ? `morning run only, ${morningAt}` : runs === 'evening' ? `afternoon run only, ${eveningAt}` : `${morningAt} & ${eveningAt}`}`;

  const checklist = [
    {
      done: !!firstChild.name.trim(),
      label: firstChild.name.trim()
        ? `${children.filter((c) => c.name.trim()).map((c) => [c.name.trim(), c.classLabel.trim()].filter(Boolean).join(' · ')).join('  •  ')}`
        : 'Child · who travels',
    },
    { done: !!home, label: home ? `Home · pin dropped (${home.lat.toFixed(4)}, ${home.lng.toFixed(4)})` : 'Home · where the run starts' },
    { done: step > 3, label: `Hours · ${hoursLine}` },
    { done: step >= 4, label: `Plan · ${FAMILY_PRICE_LABEL}` },
  ];

  /** Whose phone the walkthrough is for — the person you are asking to accept it. */
  const driverLabel = driverMode === 'private_van'
    ? (vanCompany.trim() || 'THE VAN').split(/\s+/)[0].toUpperCase()
    : driverName.trim()
      ? driverName.trim().split(/\s+/)[0].toUpperCase()
      : 'YOUR DRIVER';

  /* ------------------------------------------------------------- styling -- */
  const css = `
    ${FAMILY_FONT_CSS}
    .fam-root { min-height: 100vh; background: ${FAM.bg}; color: ${FAM.ink}; font-family: ${UI_FONT}; position: relative; overflow-x: hidden; }
    .fam-blob { position: absolute; border-radius: 50%; pointer-events: none; }
    .fam-chrome { position: relative; display: flex; align-items: center; gap: 14px; min-height: 58px; padding: 0 26px; background: rgba(255,255,255,.78); border-bottom: 1px solid ${FAM.line}; backdrop-filter: blur(8px); }
    @media (max-width: 480px) { .fam-chrome { padding: 0 14px; gap: 9px; } }
    .fam-grid { position: relative; display: grid; grid-template-columns: 1.02fr .98fr; gap: 44px; padding: 40px 44px; max-width: 1180px; margin: 0 auto; align-items: start; }
    @media (max-width: 960px) { .fam-grid { grid-template-columns: 1fr; gap: 26px; padding: 28px 20px; } }
    @media (max-width: 430px) { .fam-grid { padding: 20px 14px; } }
    .fam-display { font-family: ${DISPLAY_FONT}; font-weight: 600; font-size: 40px; line-height: 1.06; letter-spacing: -.01em; margin: 12px 0 0; }
    @media (max-width: 600px) { .fam-display { font-size: 29px; } }
    .fam-input { width: 100%; box-sizing: border-box; padding: 12px 14px; border-radius: 12px; border: 1.5px solid ${FAM.line}; background: #fff; font: inherit; font-size: 15px; color: ${FAM.ink}; outline: none; transition: border-color .15s, box-shadow .15s; }
    .fam-input::placeholder { color: ${FAM.muted}; }
    .fam-input:focus { border-color: ${FAM.accent}; box-shadow: 0 0 0 3px rgba(0,112,127,.16); }
    .fam-input[data-bad="1"] { border-color: ${FAM.danger}; }
    .fam-two { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
    @media (max-width: 560px) { .fam-two { grid-template-columns: 1fr; } }
    .fam-cta { display: inline-flex; align-items: center; gap: 9px; height: 48px; padding: 0 24px; border: none; border-radius: 8px; background: ${FAM_GRADIENT}; color: #fff; font: inherit; font-size: 15px; font-weight: 700; cursor: pointer; box-shadow: 0 14px 34px -14px rgba(0,112,127,.6); transition: transform .12s, opacity .15s; }
    .fam-cta:hover:not(:disabled) { transform: translateY(-1px); }
    .fam-cta:disabled { opacity: .45; cursor: not-allowed; box-shadow: none; }
    .fam-ghost { background: none; border: none; font: inherit; font-size: 13.5px; font-weight: 600; color: ${FAM.ink3}; cursor: pointer; padding: 8px 4px; }
    .fam-ghost:hover { color: ${FAM.accent}; }
    .fam-pick { display: flex; align-items: center; gap: 14px; padding: 16px 17px; border-radius: 16px; width: 100%; text-align: left; font: inherit; cursor: pointer; background: #fff; border: 1.5px solid ${FAM.line}; color: ${FAM.ink}; transition: border-color .15s, background .15s, box-shadow .15s; }
    .fam-pick:hover { border-color: ${FAM.accentPale}; }
    .fam-pick[data-on="1"] { background: linear-gradient(120deg,#E2F5F8,#E2F5F8); border: 2px solid ${FAM.accent}; box-shadow: 0 10px 26px -14px rgba(0,112,127,.5); padding: 15px 16px; }
    .fam-day { min-width: 52px; padding: 9px 6px; border-radius: 11px; border: 1.5px solid ${FAM.line}; background: #fff; font: inherit; font-size: 13px; font-weight: 700; color: ${FAM.ink3}; cursor: pointer; }
    .fam-day[data-on="1"] { background: ${FAM.tint}; border-color: ${FAM.accent}; color: ${FAM.accent}; }
    .fam-panel { display: flex; flex-direction: column; gap: 14px; position: sticky; top: 22px; }
    @media (max-width: 960px) { .fam-panel { position: static; } }
    .fam-seg { width: 26px; height: 4px; border-radius: 99px; }
    @media (max-width: 620px) { .fam-seg { display: none; } }
  `;

  /* ============================================================== done == */
  if (phase === 'done' && result) {
    return (
      <div className="fam-root">
        <style>{css}</style>
        <div className="fam-blob" style={{ top: -180, left: -120, width: 520, height: 520, background: 'radial-gradient(circle, rgba(15,184,206,.2), transparent 68%)' }} />
        <div className="fam-chrome">
          <Link to="/" style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-.02em', color: FAM.ink, textDecoration: 'none' }}>Trasse</Link>
          <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 6, background: FAM.tint, color: FAM.accent, fontSize: 11, fontWeight: 800, letterSpacing: '.06em' }}>FAMILIES</span>
        </div>
        <div style={{ position: 'relative', display: 'grid', placeItems: 'center', padding: '60px 20px' }}>
          <div style={{ width: '100%', maxWidth: 560, background: '#fff', border: `1px solid ${FAM.line}`, borderRadius: 24, padding: 'clamp(24px,5vw,40px)', boxShadow: '0 24px 60px -30px rgba(76,52,148,.35)' }}>
            <div style={{ width: 52, height: 52, borderRadius: 16, background: FAM.posTint, display: 'grid', placeItems: 'center', marginBottom: 18 }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={FAM.pos} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7.5 12 13l9-5.5" /><rect x="3" y="5" width="18" height="14" rx="2.5" /></svg>
            </div>
            <h1 className="fam-display" style={{ margin: 0, fontSize: 34 }}>Check your email</h1>
            <p style={{ margin: '14px 0 0', fontSize: 16, lineHeight: 1.6, color: FAM.ink2 }}>
              Set your password to start your {FAMILY_PILOT_DAYS} free days. We sent the link to{' '}
              <b style={{ color: FAM.ink }}>{result.email}</b>.
            </p>
            <div style={{ marginTop: 20, padding: '14px 16px', borderRadius: 14, background: FAM.tint, border: `1px solid ${FAM.tint2}` }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '.1em', color: FAM.accent }}>YOUR HOUSEHOLD</div>
              <div style={{ marginTop: 4, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 15, fontWeight: 700, color: FAM.ink }}>{result.orgCode}</div>
            </div>
            <p style={{ margin: '20px 0 0', fontSize: 14.5, lineHeight: 1.65, color: FAM.ink2 }}>
              Your {FAMILY_PILOT_DAYS} free days begin when you set your password — not now — so nothing is
              ticking while that email sits unread. No card is needed to start.
            </p>
            {!result.emailed && (
              <p style={{ margin: '14px 0 0', fontSize: 13.5, lineHeight: 1.6, color: FAM.amber, background: FAM.amberTint, padding: '11px 13px', borderRadius: 12 }}>
                Your household was created, but we could not confirm the email went out. Give it a
                couple of minutes, then use <b>Resend the email</b> below — and if that still brings
                nothing, email {SUPPORT_EMAIL} with your household name.
              </p>
            )}
            <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${FAM.line}`, fontSize: 13.5, lineHeight: 1.65, color: FAM.ink3 }}>
              <b style={{ color: FAM.ink }}>No email after a few minutes?</b> Check your spam folder — it comes
              from <span style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>noreply@trasseapp.com</span>.
              Don&apos;t sign up a second time — one free trial per email, so that sends nothing. Ask for the
              same link again instead; it always goes to the address above.
              <div style={{ marginTop: 14 }}>
                <button
                  type="button"
                  className="fam-ghost"
                  onClick={() => void handleResend()}
                  disabled={resending || resendCooldown > 0}
                  style={{
                    border: `1.5px solid ${resending || resendCooldown > 0 ? FAM.line : FAM.accentPale}`,
                    borderRadius: 999, padding: '9px 18px', background: '#fff',
                    color: resending || resendCooldown > 0 ? FAM.muted : FAM.accent,
                    cursor: resending || resendCooldown > 0 ? 'default' : 'pointer',
                  }}
                >
                  {resending ? 'Sending…' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend the email'}
                </button>
              </div>
              {resendNote && (
                <div
                  role="status"
                  style={{
                    marginTop: 12, padding: '11px 13px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.6,
                    background: resendNote.tone === 'sent' ? FAM.posTint : resendNote.tone === 'settled' ? FAM.tint : FAM.dangerTint,
                    color: resendNote.tone === 'sent' ? FAM.pos : resendNote.tone === 'settled' ? FAM.accent : FAM.danger,
                  }}
                >
                  {resendNote.text}
                  {resendNote.tone === 'settled' && (
                    <>{' '}<Link to="/login" style={{ color: FAM.accent, fontWeight: 700 }}>Sign in</Link>.</>
                  )}
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                Still nothing after that? Email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: FAM.accent, fontWeight: 700 }}>{SUPPORT_EMAIL}</a>{' '}
                with your household name and we will send it by hand.
              </div>
            </div>
            <div style={{ marginTop: 24 }}>
              <Link to="/" className="fam-cta" style={{ textDecoration: 'none' }}>Back to trasse <Arrow /></Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================== form == */
  const stepMeta = [
    { eyebrow: 'YOU & YOUR CHILD', title: 'Who is the school run for?' },
    { eyebrow: 'WHO DRIVES', title: 'Who is behind the wheel?' },
    { eyebrow: 'HOURS', title: 'When does the car go out?' },
    { eyebrow: 'REVIEW', title: 'One last look before we start.' },
  ][step - 1];

  const sending = phase === 'sending';

  return (
    <div className="fam-root">
      <style>{css}</style>
      <div className="fam-blob" style={{ top: -180, left: -120, width: 520, height: 520, background: 'radial-gradient(circle, rgba(15,184,206,.2), transparent 68%)' }} />
      <div className="fam-blob" style={{ bottom: -200, right: -140, width: 520, height: 520, background: 'radial-gradient(circle, rgba(0,112,127,.12), transparent 68%)' }} />

      {/* ---- chrome ---- */}
      <div className="fam-chrome">
        <Link to="/" style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-.02em', color: FAM.ink, textDecoration: 'none' }}>Trasse</Link>
        <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 6, background: FAM.tint, color: FAM.accent, fontSize: 11, fontWeight: 800, letterSpacing: '.06em' }}>FAMILIES</span>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {[1, 2, 3, 4].map((n) => (
            <span key={n} className="fam-seg" style={{ background: n <= step ? FAM_GRADIENT : '#e4dff0' }} />
          ))}
          <span style={{ fontSize: 12, fontWeight: 700, color: FAM.ink3, paddingLeft: 8, whiteSpace: 'nowrap' }}>Step {step} of 4</span>
        </div>
      </div>

      <div className="fam-grid">
        {/* ================= LEFT — the question ================= */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Somebody vouched for Trasse to get this parent here. Saying so is
              the single strongest thing on the page — and it is only ever the
              referrer's display label, never a contact detail of theirs. */}
          {referrer && step === 1 && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 9, alignSelf: 'flex-start',
              padding: '7px 13px', borderRadius: 999, marginBottom: 14,
              background: FAM.tint, border: `1px solid ${FAM.tint2}`,
              color: FAM.ink, fontSize: 13.5, fontWeight: 600,
            }}>
              <span aria-hidden style={{ fontSize: 15 }}>👋</span>
              <span>
                <b>{referrer.label}</b> {referrer.kind === 'school' ? 'recommended' : 'invited you to'} Trasse
              </span>
            </div>
          )}
          <Eyebrow>{stepMeta.eyebrow}</Eyebrow>
          <h2 className="fam-display">{stepMeta.title}</h2>

          {/* ---------------- step 1 ---------------- */}
          {step === 1 && (
            <>
              <Lede>
                We set the household up in your name. Your child is why you are here — what Trasse
                actually follows is the car on the school run.
              </Lede>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 24 }}>
                <Field label="Your name" error={errFor('parentName', 1)}>
                  <input className="fam-input" data-bad={errFor('parentName', 1) ? '1' : '0'} value={parentName}
                    onChange={(e) => { setParentName(e.target.value); clearServerErr(); }}
                    placeholder="Grace Okello" autoComplete="name" />
                </Field>

                <div className="fam-two">
                  <Field label="Email" hint="Where your login link goes." error={errFor('email', 1)}>
                    <input className="fam-input" data-bad={errFor('email', 1) ? '1' : '0'} value={email} type="email"
                      onChange={(e) => { setEmail(e.target.value); clearServerErr(); }}
                      placeholder="grace@example.com" autoComplete="email" />
                  </Field>
                  <Field label="Phone" error={errFor('phone', 1)}>
                    <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                      <select
                        aria-label="Country code"
                        value={phoneDial}
                        onChange={(e) => { setPhoneDial(e.target.value); clearServerErr(); }}
                        style={dialSelectStyle}
                      >
                        {FAMILY_DIALS.map((d) => (
                          <option key={d.dial} value={d.dial}>{d.iso} {d.dial}</option>
                        ))}
                      </select>
                      <input className="fam-input" data-bad={errFor('phone', 1) ? '1' : '0'} value={phone} inputMode="tel"
                        onChange={(e) => { setPhone(e.target.value); clearServerErr(); }}
                        placeholder={phoneDial === '+254' ? '722 000 000' : 'Phone number'} autoComplete="tel-national" />
                    </div>
                  </Field>
                </div>

                <Field
                  label="Family name"
                  hint={orgCode
                    ? <>Your household will be <b style={{ fontFamily: 'ui-monospace, Menlo, monospace', color: FAM.accent }}>{orgCode}</b></>
                    : 'One word is enough — "Okello", "Wanjiru".'}
                  error={errFor('householdName', 1)}
                >
                  <input className="fam-input" data-bad={errFor('householdName', 1) ? '1' : '0'} value={householdName}
                    onChange={(e) => { setHouseholdName(e.target.value); clearServerErr(); }}
                    placeholder="Okello" />
                </Field>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {children.map((c, i) => (
                    <div key={i} className="fam-two">
                      <Field label={children.length > 1 ? `Child ${i + 1}` : 'Your child'} error={i === 0 ? errFor('children', 1) : null}>
                        <input className="fam-input" value={c.name}
                          onChange={(e) => setChild(i, { name: e.target.value })}
                          placeholder="Amara Okello" />
                      </Field>
                      <Field label="Class" hint="Optional — helps the driver call the right child.">
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input className="fam-input" value={c.classLabel}
                            onChange={(e) => setChild(i, { classLabel: e.target.value })}
                            placeholder="Grade 4" />
                          {children.length > 1 && (
                            <button type="button" className="fam-ghost" onClick={() => setChildren((prev) => prev.filter((_, idx) => idx !== i))}
                              style={{ flex: '0 0 auto', color: FAM.danger }}>Remove</button>
                          )}
                        </div>
                      </Field>
                    </div>
                  ))}
                  {children.length < MAX_CHILDREN && (
                    <button type="button" className="fam-ghost" style={{ alignSelf: 'flex-start', color: FAM.accent }}
                      onClick={() => setChildren((prev) => [...prev, { name: '', classLabel: '' }])}>
                      + Add another child
                    </button>
                  )}
                  <span style={{ fontSize: 12.5, color: FAM.muted }}>{FAMILY_CHILD_CAP_HINT}</span>
                </div>

                <Field label="Home" hint="Where the morning run starts. Drop the pin on your gate, not the road." error={errFor('home', 1)}>
                  <LocationPicker value={home} onChange={(v) => { setHome(v); clearServerErr(); }} heightPx={230} />
                </Field>

                {/* The code a family or a school handed them. It used to be
                    readable only out of the link's ?ref=, so a code copied
                    from the app and sent as plain text had nowhere to go and
                    the referrer quietly lost their reward. The introduction
                    is recorded once, at activation, and cannot be added
                    afterwards — which is exactly why this box has to exist. */}
                <Field
                  label="Referral code (optional)"
                  hint={
                    referralKnown === false
                      ? undefined
                      : 'From the family or school who told you about Trasse. Leave it blank if nobody did.'
                  }
                  error={referralKnown === false ? "We don't recognise that code — check it with whoever gave it to you." : null}
                >
                  <input
                    className="fam-input"
                    value={referralCode}
                    data-bad={referralKnown === false ? '1' : '0'}
                    onChange={(e) => setReferralCode(e.target.value.trim().toUpperCase())}
                    placeholder="OKELLO-4K2"
                    autoComplete="off"
                    spellCheck={false}
                    style={{ textTransform: 'uppercase', letterSpacing: '.06em', maxWidth: 260 }}
                  />
                </Field>

                <div style={{ borderTop: `1px solid ${FAM.line}`, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Field label="School (optional)" hint="The school pin is what ends the morning run. You can add it later.">
                    <input className="fam-input" value={schoolName} onChange={(e) => setSchoolName(e.target.value)} placeholder="Riverside Academy" />
                  </Field>
                  {showSchoolPin ? (
                    <LocationPicker value={school} onChange={setSchool} heightPx={210} label="School gate" />
                  ) : (
                    <button type="button" className="fam-ghost" style={{ alignSelf: 'flex-start', color: FAM.accent }} onClick={() => setShowSchoolPin(true)}>
                      + Pin the school gate too
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ---------------- step 2 ---------------- */}
          {step === 2 && (
            <>
              <Lede>
                Whoever drives gets the driver app. They sign in with the email you give below — a phone number alone cannot open the app.
              </Lede>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginTop: 26 }}>
                {DRIVER_CARDS.map((c) => {
                  const on = driverMode === c.id;
                  return (
                    <button key={c.id} type="button" className="fam-pick" data-on={on ? '1' : '0'}
                      onClick={() => { setDriverMode(c.id); clearServerErr(); }}>
                      <span style={{ color: on ? FAM.accent : FAM.ink3, flex: '0 0 auto', display: 'flex' }}>{c.icon}</span>
                      <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                        <span style={{ fontSize: 15.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {c.title}
                        </span>
                        <span style={{ fontSize: 13, color: FAM.ink2 }}>{c.sub}</span>
                      </span>
                      {on && (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={FAM.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9.5" /><path d="M8 12.4l2.6 2.6L16.2 9.4" /></svg>
                      )}
                    </button>
                  );
                })}
              </div>
              {serverErr?.field === 'driverMode' && (
                <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: FAM.danger }}>{serverErr.message}</div>
              )}
              {touched[2] && !driverMode && (
                <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: FAM.danger }}>Tell us who drives.</div>
              )}

              {(driverMode === 'private_driver' || driverMode === 'private_van') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 26 }}>
                  {driverMode === 'private_van' && (
                    <>
                      <Field label="Van service" hint="The company, not the driver — they change who drives." error={errFor('vanCompany', 2)}>
                        <input className="fam-input" data-bad={errFor('vanCompany', 2) ? '1' : '0'} value={vanCompany}
                          onChange={(e) => { setVanCompany(e.target.value); clearServerErr(); }} placeholder="Karen Shuttle" />
                      </Field>
                      <div className="fam-two">
                        <Field label="Seats (optional)" hint="How many children the van carries.">
                          <input className="fam-input" value={vanSeats} inputMode="numeric"
                            onChange={(e) => setVanSeats(e.target.value.replace(/[^0-9]/g, ''))} placeholder="11" />
                        </Field>
                        <Field label="Their office number (optional)" hint="Who you call when the van is late.">
                          <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                            <select
                              aria-label="Van office country code"
                              value={vanDial}
                              onChange={(e) => setVanDial(e.target.value)}
                              style={dialSelectStyle}
                            >
                              {FAMILY_DIALS.map((d) => (
                                <option key={d.dial} value={d.dial}>{d.iso} {d.dial}</option>
                              ))}
                            </select>
                            <input className="fam-input" value={vanPhone} inputMode="tel"
                              onChange={(e) => setVanPhone(e.target.value)}
                              placeholder={vanDial === '+254' ? '733 000 000' : 'Phone number'} />
                          </div>
                        </Field>
                      </div>
                    </>
                  )}
                  {driverMode === 'private_driver' && (
                    <>
                      <Field label="Driver's name" error={errFor('driverName', 2)}>
                        <input className="fam-input" data-bad={errFor('driverName', 2) ? '1' : '0'} value={driverName}
                          onChange={(e) => { setDriverName(e.target.value); clearServerErr(); }} placeholder="Peter Kimani" />
                      </Field>
                      <div className="fam-two">
                        <Field label="Driver's phone" hint="How you reach them if the run is late. Not how they sign in." error={errFor('driverPhone', 2)}>
                          <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                            <select
                              aria-label="Driver country code"
                              value={driverDial}
                              onChange={(e) => { setDriverDial(e.target.value); clearServerErr(); }}
                              style={dialSelectStyle}
                            >
                              {FAMILY_DIALS.map((d) => (
                                <option key={d.dial} value={d.dial}>{d.iso} {d.dial}</option>
                              ))}
                            </select>
                            <input className="fam-input" data-bad={errFor('driverPhone', 2) ? '1' : '0'} value={driverPhone} inputMode="tel"
                              onChange={(e) => { setDriverPhone(e.target.value); clearServerErr(); }}
                              placeholder={driverDial === '+254' ? '733 000 000' : 'Phone number'} />
                          </div>
                        </Field>
                        <Field label="Driver's email" hint="This is their login. We create the driver app account on this address." error={errFor('driverEmail', 2)}>
                          <input className="fam-input" data-bad={errFor('driverEmail', 2) ? '1' : '0'} value={driverEmail} type="email"
                            onChange={(e) => { setDriverEmail(e.target.value); clearServerErr(); }} placeholder="peter@example.com" />
                        </Field>
                      </div>
                    </>
                  )}

                  <div className="fam-two">
                    <Field label="Number plate" hint="Kenyan format, e.g. KDA442K." error={errFor('vehiclePlate', 2)}>
                      <input className="fam-input" data-bad={errFor('vehiclePlate', 2) ? '1' : '0'} value={plate}
                        onChange={(e) => { setPlate(e.target.value.toUpperCase()); clearServerErr(); }}
                        onBlur={() => setPlate((p) => normalizePlate(p))}
                        placeholder="KDA 442K" style={{ letterSpacing: '.06em', fontWeight: 700 }} />
                    </Field>
                    {driverMode !== 'private_van' && (
                      <Field label="Car nickname (optional)" hint="What you call it at home.">
                        <input className="fam-input" value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="The blue Toyota" />
                      </Field>
                    )}
                  </div>

                  <Field label="Fuel">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {FUEL_TYPES.map((f) => (
                        <button key={f.id} type="button" className="fam-day" data-on={fuelType === f.id ? '1' : '0'}
                          onClick={() => setFuelType(f.id)} style={{ minWidth: 74 }}>{f.label}</button>
                      ))}
                    </div>
                  </Field>
                </div>
              )}
            </>
          )}

          {/* ---------------- step 3 ---------------- */}
          {step === 3 && (
            <>
              <Lede>
                These are the hours the car is on duty. Outside them Trasse is not watching anything —
                that is the whole point of setting them.
              </Lede>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 22, marginTop: 26 }}>
                <Field label="School days" error={errFor('schedule', 3)}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {WEEKDAYS.map((d) => {
                      const on = days.includes(d.key);
                      return (
                        <button key={d.key} type="button" className="fam-day" data-on={on ? '1' : '0'}
                          onClick={() => { setDays((prev) => (on ? prev.filter((x) => x !== d.key) : [...prev, d.key])); clearServerErr(); }}>
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                </Field>

                {/* Some drivers only do the morning run, some only the afternoon.
                    The run the car does not do is off every day. */}
                <Field
                  label="Which runs?"
                  hint={runs === 'both'
                    ? 'The car does the morning and the afternoon run.'
                    : runs === 'morning'
                      ? 'Only the morning run. Someone else brings them home.'
                      : 'Only the afternoon run. Someone else takes them in the morning.'}
                >
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {([['both', 'Both runs'], ['morning', 'Morning only'], ['evening', 'Afternoon only']] as const).map(([k, label]) => (
                      <button key={k} type="button" className="fam-day" data-on={runs === k ? '1' : '0'}
                        onClick={() => { setRuns(k); clearServerErr(); }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </Field>

                <div className={runs === 'both' ? 'fam-two' : undefined}>
                  {runs !== 'evening' && (
                    <Field label="Morning pickup" hint="When the car leaves home.">
                      <input className="fam-input" type="time" value={morningAt}
                        onChange={(e) => { setMorningAt(e.target.value); clearServerErr(); }} />
                    </Field>
                  )}
                  {runs !== 'morning' && (
                    <Field label="Afternoon pickup" hint="When it leaves school.">
                      <input className="fam-input" type="time" value={eveningAt}
                        onChange={(e) => { setEveningAt(e.target.value); clearServerErr(); }} />
                    </Field>
                  )}
                </div>

                <div style={{ padding: '14px 16px', borderRadius: 14, background: FAM.tint, border: `1px solid ${FAM.tint2}`, fontSize: 13.5, lineHeight: 1.6, color: FAM.ink2 }}>
                  You can change these any time from <b style={{ color: FAM.ink }}>Hours</b> in your dashboard. A run
                  that starts outside these times still records — the hours are what we expect, not a lock.
                </div>
              </div>
            </>
          )}

          {/* ---------------- step 4 ---------------- */}
          {step === 4 && (
            <>
              <Lede>Everything below is what we will set up. Nothing is charged today.</Lede>
              <div style={{ marginTop: 24, borderRadius: 18, border: `1px solid ${FAM.line}`, background: '#fff', overflow: 'hidden' }}>
                {[
                  { k: 'Household', v: <>{householdName.trim() || '—'} <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', color: FAM.accent, fontSize: 12.5 }}>{orgCode}</span></> },
                  { k: 'You', v: `${parentName.trim() || '—'} · ${email.trim().toLowerCase() || '—'} · ${e164(phoneDial, phone)}` },
                  { k: children.filter((c) => c.name.trim()).length > 1 ? 'Children' : 'Child', v: children.filter((c) => c.name.trim()).map((c) => [c.name.trim(), c.classLabel.trim()].filter(Boolean).join(' · ')).join('  •  ') || '—' },
                  { k: 'Home', v: home ? `Pinned · ${home.lat.toFixed(5)}, ${home.lng.toFixed(5)}` : 'Not set' },
                  { k: 'School', v: schoolName.trim() ? `${schoolName.trim()}${school ? ' · gate pinned' : ' · no pin yet'}` : school ? 'Gate pinned' : 'Add later' },
                  { k: 'Who drives', v: driverMode === 'private_van'
                    ? (vanCompany.trim() || 'A van service')
                    : `${driverName.trim()} — a driver you employ` },
                  ...(driverMode === 'private_driver'
                    ? [{ k: 'Driver signs in with', v: driverEmail.trim().toLowerCase() || '—' }]
                    : []),
                  { k: 'Vehicle', v: `${cleanPlate}${nickname.trim() ? ` · ${nickname.trim()}` : ''} · ${FUEL_TYPES.find((f) => f.id === fuelType)?.label}` },
                  { k: 'Hours', v: hoursLine },
                ].map((row, i) => (
                  <div key={row.k} style={{ display: 'flex', gap: 16, padding: '13px 17px', borderTop: i === 0 ? 'none' : `1px solid ${FAM.line}`, fontSize: 14, flexWrap: 'wrap' }}>
                    <span style={{ flex: '0 0 132px', color: FAM.muted, fontWeight: 600 }}>{row.k}</span>
                    <span style={{ flex: 1, minWidth: 160, fontWeight: 600, color: FAM.ink, wordBreak: 'break-word' }}>{row.v}</span>
                  </div>
                ))}
              </div>

              {/* ---- privacy: the promise, in full, before they commit ---- */}
              <div style={{ marginTop: 22, borderRadius: 18, overflow: 'hidden', border: `1px solid ${FAM.line}` }}>
                <div style={{ padding: '16px 18px', background: FAM.posTint }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14.5, fontWeight: 800, color: FAM.pos }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={FAM.pos} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9.5" /><path d="M12 7.5v5l3 1.8" /></svg>
                    When Trasse tracks
                  </div>
                  <ul style={{ margin: '10px 0 0', paddingLeft: 20, fontSize: 13.5, lineHeight: 1.7, color: FAM.ink2 }}>
                    {TRACKING_ON.map((t) => <li key={t}>{t}</li>)}
                  </ul>
                </div>
                <div style={{ padding: '16px 18px', background: '#fff', borderTop: `1px solid ${FAM.line}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14.5, fontWeight: 800, color: FAM.ink }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={FAM.ink3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9.5" /><path d="M8.6 8.6l6.8 6.8" /></svg>
                    When Trasse does not track
                  </div>
                  <ul style={{ margin: '10px 0 0', paddingLeft: 20, fontSize: 13.5, lineHeight: 1.7, color: FAM.ink2 }}>
                    {TRACKING_OFF.map((t) => <li key={t}>{t}</li>)}
                  </ul>
                </div>
              </div>

              {/* ---- the plan line ---- */}
              <div style={{ marginTop: 22, padding: '18px 20px', borderRadius: 18, background: FAM.tint, border: `1px solid ${FAM.tint2}` }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.12em', color: FAM.accent }}>YOUR PLAN</div>
                <div className="fam-display" style={{ fontSize: 27, margin: '8px 0 0' }}>{FAMILY_PRICE_LABEL}</div>
                <div style={{ marginTop: 6, fontSize: 14.5, fontWeight: 700, color: FAM.ink2 }}>
                  after {FAMILY_PILOT_DAYS} free days · no card needed to start
                </div>
                {/* 2026-09-13: this said "Everything is included… there is no second tier to
                    buy", which stopped being true when the base was capped at two children and
                    analytics moved to a paid add-on — and it contradicted the child-count hint
                    three steps earlier in this same wizard. The last screen before a parent
                    commits is the worst place to overstate what they are buying. The "day 15"
                    line was stale too: the Families pilot is FAMILY_PILOT_DAYS, not fourteen. */}
                <div style={{ marginTop: 10, fontSize: 13.5, lineHeight: 1.6, color: FAM.ink2 }}>
                  Live tracking, the driver app, run history and alerts — for two children.
                  Analytics and extra children are KES {FAMILY_ADDON_KES.toLocaleString()}/month
                  more, whenever you want them. We ask for a card when the free days end, not
                  before, and you can stop then.
                </div>
              </div>

              {serverErr && !serverErr.field && (
                <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 12, background: FAM.dangerTint, color: FAM.danger, fontSize: 13.5, fontWeight: 600 }}>
                  {serverErr.message}
                </div>
              )}
            </>
          )}

          {/* ---- actions ---- */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 26, flexWrap: 'wrap' }}>
            {step < 4 ? (
              <button type="button" className="fam-cta" onClick={() => void next()}
                disabled={checking || (touched[step] && !stepOk(step))}>
                {checking ? 'Checking…' : <>Continue <Arrow /></>}
              </button>
            ) : (
              <button type="button" className="fam-cta" onClick={submit} disabled={sending}>
                {sending ? 'Setting up your household…' : `Start my ${FAMILY_PILOT_DAYS} free days`}
                {!sending && <Arrow />}
              </button>
            )}
            {step > 1 && <button type="button" className="fam-ghost" onClick={back}>Back</button>}
          </div>
          {step === 4 && (
            <div style={{ marginTop: 12, fontSize: 12.5, color: FAM.muted, lineHeight: 1.6 }}>
              By starting you agree to the <Link to="/terms" style={{ color: FAM.accent, fontWeight: 600 }}>Terms</Link> and{' '}
              <Link to="/privacy" style={{ color: FAM.accent, fontWeight: 600 }}>Privacy policy</Link>.
            </div>
          )}
        </div>

        {/* ================= RIGHT — what the driver sees + so far ================= */}
        <div className="fam-panel">
          <div style={{ borderRadius: 22, background: `linear-gradient(120deg, ${FAM.nightTop}, ${FAM.nightBottom})`, border: '1px solid rgba(169,199,255,.2)', padding: 18, boxShadow: '0 24px 54px -22px rgba(31,20,55,.7)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 34, height: 34, borderRadius: 11, background: FAM_GRADIENT, display: 'grid', placeItems: 'center', flex: '0 0 34px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="4.5" y="2.5" width="15" height="19" rx="3" /><path d="M10 18.5h4" /></svg>
              </span>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.1em', color: FAM.nightLabel }}>WHAT {driverLabel} WILL SEE</span>
                <span style={{ fontSize: 13, color: FAM.nightInk }}>30-second walkthrough</span>
              </div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 28, padding: '0 11px', borderRadius: 999, background: 'rgba(255,255,255,.1)', fontSize: 11.5, fontWeight: 700, color: '#f1f5ff' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#f1f5ff"><path d="M8 5.2v13.6L19 12 8 5.2Z" /></svg>Play
              </span>
            </div>
            <div style={{ marginTop: 14, borderRadius: 16, overflow: 'hidden', background: '#0b0f1c', border: '1px solid rgba(169,199,255,.14)', aspectRatio: '16 / 10', position: 'relative', display: 'grid', placeItems: 'center' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(135deg, rgba(169,199,255,.05) 0 12px, transparent 12px 24px)' }} />
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 54, height: 54, borderRadius: '50%', background: FAM_GRADIENT, display: 'grid', placeItems: 'center', boxShadow: '0 14px 30px -12px rgba(0,112,127,.8)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff"><path d="M8 5.2v13.6L19 12 8 5.2Z" /></svg>
                </span>
                <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, letterSpacing: '.06em', color: '#8e85ad' }}>driver-onboarding.mp4 · Remotion</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              {[0, 1, 2].map((i) => (
                <span key={i} style={{ flex: 1, height: 3, borderRadius: 99, background: i === 0 ? FAM_GRADIENT : 'rgba(255,255,255,.14)' }} />
              ))}
            </div>
            <p style={{ margin: '12px 0 0', fontSize: 12, lineHeight: 1.55, color: '#8e85ad' }}>
              The clip is being recorded — it will play here before launch. It shows the driver exactly
              what the app asks for, and what it does not.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 18, borderRadius: 20, background: 'rgba(255,255,255,.86)', border: '1px solid #fff', boxShadow: '0 12px 30px rgba(76,52,148,.1)' }}>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.12em', color: FAM.ink3 }}>SO FAR</span>
            {checklist.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, opacity: item.done ? 1 : .55 }}>
                <CheckIcon on={item.done} />
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: FAM.ink, lineHeight: 1.45, wordBreak: 'break-word' }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
